import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import {
  LayoutDashboard, Users, MessagesSquare, BadgeCheck, Store, CreditCard,
  Send, Inbox, TrendingUp, Server, SlidersHorizontal, Menu, X,
} from "lucide-react";

import Overview from "./admin/Overview";
import UsersTab from "./admin/UsersTab";
import CommunityTab from "./admin/CommunityTab";
import ProfessionalsTab from "./admin/ProfessionalsTab";
import MarketplaceTab from "./admin/MarketplaceTab";
import BillingTab from "./admin/BillingTab";
import CommunicationsTab from "./admin/CommunicationsTab";
import FeedbackSupportTab from "./admin/FeedbackSupportTab";
import MarketingTab from "./admin/MarketingTab";
import SystemTab from "./admin/SystemTab";
import SettingsTab from "./admin/SettingsTab";

const SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, Comp: Overview },
  { key: "users", label: "Users", icon: Users, Comp: UsersTab },
  { key: "community", label: "Community", icon: MessagesSquare, Comp: CommunityTab },
  { key: "professionals", label: "Professionals", icon: BadgeCheck, Comp: ProfessionalsTab },
  { key: "marketplace", label: "Marketplace", icon: Store, Comp: MarketplaceTab },
  { key: "billing", label: "Billing", icon: CreditCard, Comp: BillingTab },
  { key: "communications", label: "Communications", icon: Send, Comp: CommunicationsTab },
  { key: "feedback", label: "Feedback", icon: Inbox, Comp: FeedbackSupportTab },
  { key: "growth", label: "Growth", icon: TrendingUp, Comp: MarketingTab },
  { key: "system", label: "System", icon: Server, Comp: SystemTab },
  { key: "settings", label: "Settings", icon: SlidersHorizontal, Comp: SettingsTab },
];

// Mobile-first priority: what actually matters from a phone.
const MOBILE_PRIORITY = ["overview", "users", "community", "system", "settings"];

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [deepTab, setDeepTab] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [attention, setAttention] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
    else if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const load = () => api.get("/admin/needs-attention")
      .then(r => setAttention(r.data)).catch(() => setAttention(null));
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [user, section]);

  const go = (nextSection, tab = null) => {
    setSection(nextSection);
    setDeepTab(tab);
    setMenuOpen(false);
    window.scrollTo({ top: 0 });
  };

  const active = useMemo(() => SECTIONS.find(s => s.key === section) || SECTIONS[0], [section]);
  const mobileSections = useMemo(
    () => MOBILE_PRIORITY.map(k => SECTIONS.find(s => s.key === k)).filter(Boolean), []);

  if (!user || user.role !== "admin") return null;
  const Active = active.Comp;
  const critical = attention?.counts?.critical || 0;
  const needsCount = attention?.items?.length || 0;

  return (
    <div className="min-h-screen bg-[#F2F4F2]">
      <Header />
      <div className="max-w-[1400px] mx-auto px-3 md:px-6 py-5 md:py-6 flex gap-6">
        {/* Desktop sidebar */}
        <nav className="hidden lg:block w-52 shrink-0" data-testid="admin-sidebar">
          <p className="text-[11px] uppercase tracking-wider text-[#222]/40 font-bold px-3 mb-2">
            Command Center
          </p>
          <div className="space-y-0.5 sticky top-4">
            {SECTIONS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => go(key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${section === key ? "bg-[#222] text-white" : "text-[#222]/70 hover:bg-black/5"}`}
                data-testid={`admin-section-${key}`}>
                <Icon size={16} className="shrink-0" />
                <span className="truncate">{label}</span>
                {key === "overview" && needsCount > 0 && (
                  <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${critical ? "bg-red-500 text-white" : "bg-amber-400 text-[#222]"}`}
                    data-testid="admin-attention-badge">{needsCount}</span>
                )}
              </button>
            ))}
          </div>
        </nav>

        <main className="flex-1 min-w-0 pb-24 lg:pb-6">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setMenuOpen(true)}
              className="lg:hidden p-2 rounded-xl bg-white border border-black/10"
              data-testid="admin-menu-open" aria-label="Open admin navigation">
              <Menu size={18} />
            </button>
            <h1 className="text-xl md:text-2xl font-bold" style={{ fontFamily: "Outfit" }}
              data-testid="admin-section-title">{active.label}</h1>
            {critical > 0 && section !== "overview" && (
              <button onClick={() => go("overview")}
                className="ml-auto text-[11px] font-bold px-2.5 py-1 rounded-full bg-red-500 text-white"
                data-testid="admin-critical-jump">
                {critical} critical
              </button>
            )}
          </div>

          <Active goto={go} initialTab={deepTab} attention={attention} />
        </main>
      </div>

      {/* Mobile: priority bottom bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-black/10 z-40 flex"
        data-testid="admin-mobile-tabs">
        {mobileSections.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => go(key)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold relative ${section === key ? "text-[#245544]" : "text-[#222]/50"}`}
            data-testid={`admin-mobile-${key}`}>
            <Icon size={17} />
            {label}
            {key === "overview" && needsCount > 0 && (
              <span className={`absolute top-1 right-1/4 w-2 h-2 rounded-full ${critical ? "bg-red-500" : "bg-amber-400"}`} />
            )}
          </button>
        ))}
      </div>

      {/* Mobile: full navigation drawer */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="admin-mobile-menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <div className="relative bg-white w-72 h-full p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] uppercase tracking-wider text-[#222]/40 font-bold">Command Center</p>
              <button onClick={() => setMenuOpen(false)} className="p-1.5 rounded-lg bg-black/5"
                data-testid="admin-menu-close"><X size={16} /></button>
            </div>
            {SECTIONS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => go(key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium ${section === key ? "bg-[#222] text-white" : "text-[#222]/70"}`}
                data-testid={`admin-menu-${key}`}>
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
